import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Database, Link2, Loader2, Search, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getEntities, getInstances, type HAEntity, type HAInstance } from "@/services/api/haApi";
import type { Component } from "@/types";
import {
  createHomeAssistantBindingUpdate,
  createHomeAssistantUnbindUpdate,
  extractHomeAssistantBinding,
  inferHomeAssistantAction,
  toHomeAssistantEntitySelection,
  type HomeAssistantEntitySelection,
} from "@/services/homeAssistant/binding";

interface HomeAssistantEntityBindingModalProps {
  isOpen: boolean;
  component: Component | null;
  onClose: () => void;
  onBind: (updates: { properties: Record<string, unknown> }) => void;
  onUnbind: (updates: { properties: Record<string, unknown> }) => void;
}

const DOMAIN_OPTIONS = [
  "all",
  "sensor",
  "binary_sensor",
  "light",
  "switch",
  "climate",
  "cover",
  "lock",
  "alarm_control_panel",
  "scene",
  "button",
  "valve",
];

export const HomeAssistantEntityBindingModal: React.FC<HomeAssistantEntityBindingModalProps> = ({
  isOpen,
  component,
  onClose,
  onBind,
  onUnbind,
}) => {
  const currentBinding = useMemo(
    () => (component ? extractHomeAssistantBinding(component.properties) : null),
    [component],
  );
  const [instances, setInstances] = useState<HAInstance[]>([]);
  const [entities, setEntities] = useState<HAEntity[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("all");
  const [search, setSearch] = useState("");
  const [manualEntityId, setManualEntityId] = useState("");
  const [attribute, setAttribute] = useState("state");
  const [selection, setSelection] = useState<HomeAssistantEntitySelection | null>(null);
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setInstances([]);
    setEntities([]);
    setSelectedInstanceId(currentBinding?.instanceId ?? "");
    setSelectedDomain(currentBinding?.domain ?? "all");
    setSearch("");
    setManualEntityId(currentBinding?.entityId ?? "");
    setAttribute(currentBinding?.attribute ?? "state");
    setSelection(currentBinding);
    setLoadError(null);
    setIsLoadingInstances(true);

    getInstances()
      .then((nextInstances) => {
        setInstances(nextInstances);
        if (!currentBinding?.instanceId && nextInstances.length > 0) {
          setSelectedInstanceId(nextInstances[0]._id);
        }
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : "Home Assistant instances could not be loaded.");
      })
      .finally(() => setIsLoadingInstances(false));
  }, [currentBinding, isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedInstanceId) return;

    setIsLoadingEntities(true);
    setLoadError(null);
    const domain = selectedDomain === "all" ? undefined : selectedDomain;

    getEntities(selectedInstanceId, domain)
      .then(setEntities)
      .catch((error: unknown) => {
        setEntities([]);
        setLoadError(error instanceof Error ? error.message : "Home Assistant entities could not be loaded.");
      })
      .finally(() => setIsLoadingEntities(false));
  }, [isOpen, selectedDomain, selectedInstanceId]);

  const selectedInstance = useMemo(
    () => instances.find((instance) => instance._id === selectedInstanceId),
    [instances, selectedInstanceId],
  );

  const filteredEntities = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return entities.slice(0, 80);

    return entities
      .filter((entity) => {
        const friendlyName = entity.attributes.friendly_name;
        const label = typeof friendlyName === "string" ? friendlyName : "";
        return (
          entity.entity_id.toLowerCase().includes(normalizedSearch) ||
          label.toLowerCase().includes(normalizedSearch) ||
          entity.domain.toLowerCase().includes(normalizedSearch)
        );
      })
      .slice(0, 80);
  }, [entities, search]);

  const previewSelection = useMemo(() => {
    const entityId = manualEntityId.trim() || selection?.entityId || "";
    if (!entityId) return null;

    return {
      instanceId: selectedInstance?._id ?? selection?.instanceId,
      instanceName: selectedInstance?.instanceName ?? selection?.instanceName,
      entityId,
      domain: selection?.entityId === entityId ? selection.domain : undefined,
      displayName: selection?.entityId === entityId ? selection.displayName : undefined,
      areaName: selection?.entityId === entityId ? selection.areaName : undefined,
      state: selection?.entityId === entityId ? selection.state : undefined,
      attribute: attribute.trim() || "state",
    };
  }, [attribute, manualEntityId, selectedInstance, selection]);

  const previewAction = useMemo(() => {
    if (!component || !previewSelection) return null;
    return inferHomeAssistantAction(component.type, previewSelection);
  }, [component, previewSelection]);

  const handleEntitySelect = useCallback(
    (entity: HAEntity) => {
      const nextSelection = toHomeAssistantEntitySelection(
        entity,
        selectedInstance?._id,
        selectedInstance?.instanceName,
      );
      setSelection(nextSelection);
      setManualEntityId(nextSelection.entityId);
      setAttribute(nextSelection.attribute ?? "state");
    },
    [selectedInstance],
  );

  const handleBind = useCallback(() => {
    if (!component || !previewSelection) return;
    onBind(createHomeAssistantBindingUpdate(component, previewSelection));
    onClose();
  }, [component, onBind, onClose, previewSelection]);

  const handleUnbind = useCallback(() => {
    if (!component) return;
    onUnbind(createHomeAssistantUnbindUpdate(component));
    onClose();
  }, [component, onClose, onUnbind]);

  const componentName =
    (typeof component?.properties.title === "string" && component.properties.title) ||
    (typeof component?.properties.name === "string" && component.properties.name) ||
    (typeof component?.properties.label === "string" && component.properties.label) ||
    component?.name ||
    component?.type ||
    "component";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base text-gray-900">
            <Database className="h-4 w-4" />
            Bind Home Assistant Entity
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Selected component: {componentName}. This stores inspectable binding metadata only.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ha-instance">Instance</Label>
                <Select
                  value={selectedInstanceId || "__none__"}
                  onValueChange={(value) => setSelectedInstanceId(value === "__none__" ? "" : value)}
                >
                  <SelectTrigger id="ha-instance">
                    <SelectValue placeholder="Manual entity entry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Manual entity entry</SelectItem>
                    {instances.map((instance) => (
                      <SelectItem key={instance._id} value={instance._id}>
                        {instance.instanceName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ha-domain">Domain</Label>
                <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                  <SelectTrigger id="ha-domain">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOMAIN_OPTIONS.map((domain) => (
                      <SelectItem key={domain} value={domain}>
                        {domain === "all" ? "All domains" : domain}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ha-search">Search entities</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="ha-search"
                  className="pl-9"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="light.living_room or friendly name"
                />
              </div>
            </div>

            <div className="min-h-[180px] max-h-[260px] overflow-y-auto rounded-md border border-border">
              {(isLoadingInstances || isLoadingEntities) && (
                <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading Home Assistant data
                </div>
              )}

              {!isLoadingInstances && !isLoadingEntities && filteredEntities.length > 0 && (
                <div className="divide-y divide-border">
                  {filteredEntities.map((entity) => {
                    const friendlyName = entity.attributes.friendly_name;
                    const label = typeof friendlyName === "string" ? friendlyName : entity.entity_id;
                    const selected = manualEntityId.trim() === entity.entity_id;
                    return (
                      <button
                        key={entity.entity_id}
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted"
                        onClick={() => handleEntitySelect(entity)}
                        aria-pressed={selected}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{label}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {entity.entity_id}
                            {entity.area?.name ? ` - ${entity.area.name}` : ""}
                          </span>
                        </span>
                        <span className="shrink-0 rounded border border-border px-2 py-0.5 text-xs text-muted-foreground">
                          {entity.state}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {!isLoadingInstances && !isLoadingEntities && filteredEntities.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
                  <Database className="h-7 w-7 text-muted-foreground/50" />
                  <span>No entities loaded. Enter an entity ID manually to store a safe binding.</span>
                </div>
              )}
            </div>

            {loadError && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{loadError}</span>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
              <div className="space-y-1.5">
                <Label htmlFor="ha-entity-id">Entity ID</Label>
                <Input
                  id="ha-entity-id"
                  value={manualEntityId}
                  onChange={(event) => {
                    setManualEntityId(event.target.value);
                    setSelection(null);
                  }}
                  placeholder="sensor.living_room_temperature"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ha-attribute">Attribute</Label>
                <Input
                  id="ha-attribute"
                  value={attribute}
                  onChange={(event) => setAttribute(event.target.value)}
                  placeholder="state"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Selected entity</p>
              <p className="break-all text-sm font-medium">
                {previewSelection?.entityId || "No entity selected"}
              </p>
              {previewSelection?.displayName && (
                <p className="text-xs text-muted-foreground">{previewSelection.displayName}</p>
              )}
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Data source</p>
              <p className="text-sm">
                {previewSelection
                  ? `Home Assistant entity state, attribute ${previewSelection.attribute ?? "state"}`
                  : "Select an entity to preview"}
              </p>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Control action</p>
              <p className="text-sm">
                {previewAction
                  ? `Manual-only action: ${previewAction.serviceLabel}`
                  : "Read-only binding"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                No Home Assistant service call is executed by this picker.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          {currentBinding && (
            <Button type="button" variant="outline" onClick={handleUnbind}>
              <Unlink className="mr-2 h-4 w-4" />
              Unbind
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleBind} disabled={!previewSelection?.entityId.trim()}>
            <Link2 className="mr-2 h-4 w-4" />
            Bind Entity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
